/* eslint-disable react-refresh/only-export-components */

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Copy, Plus, Save, Zap } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { bulkCloneRiskRules, fetchAllRisks } from '@/api/risk';
import type { RuleTemplateRule } from '@/api/risk/types';
import { HelpSheet } from '@/components/shared/HelpSheet';
import { PageContainer } from '@/components/shared/PageContainer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTranslator } from '@/hooks/useTranslator';
import { useSessionStore } from '@/store/sessionStore';
import { RuleCard } from '../../../../../-components/builder/RuleCard';
import { TemplateHeaderCard } from '../../../../../-components/builder/TemplateHeaderCard';
import { ValidationSummary } from '../../../../../-components/builder/ValidationSummary';
import { useRuleTemplate } from '../../../../../-hooks/useRuleTemplate';
import { useToggleActiveRuleTemplate } from '../../../../../-hooks/useToggleActiveRuleTemplate';
import { useUpdateRuleTemplate } from '../../../../../-hooks/useUpdateRuleTemplate';

export const Route = createFileRoute(
  '/(client)/risk/(pages)/(edit)/rules/$id/edit/',
)({
  component: RuleEditPage,
});

// ─── Form schema factory ──────────────────────────────────────────────────────

const makeFormSchema = (titleRequired: string, formulaRequired: string) =>
  z.object({
    title: z.string().min(1, titleRequired).max(255),
    description: z.string().optional(),
    tags: z.array(z.string()).optional(),
    defaultSeverity: z.number().min(1).max(5),
    scoreFormula: z.string().min(1, formulaRequired),
    isActive: z.boolean(),
    visibility: z.enum(['PRIVATE', 'PUBLIC']),
  });

type FormValues = z.infer<ReturnType<typeof makeFormSchema>>;

// ─── Blank rule ───────────────────────────────────────────────────────────────

const BLANK_RULE = (): RuleTemplateRule => ({
  triggers: [
    {
      dataPoint: 'AUDITS',
      connectorType: 'is',
      assetType: '',
      operator: 'and',
    },
  ],
  offsets: [],
});

// ─── Serialize helpers ────────────────────────────────────────────────────────

/** Token JSON → plain expression string for API */
const serializeFormula = (raw: string): string => {
  if (!raw?.trim()) return '';
  try {
    const tokens = JSON.parse(raw);
    if (Array.isArray(tokens))
      return tokens.map((tk: { v: string }) => tk.v).join('');
  } catch {
    /* plain string */
  }
  return raw;
};

type TranslateFn = (
  key: string,
  vars?: Record<string, string | number>,
) => string;

function makeValidateRules(t: TranslateFn) {
  return function validateRules(rules: RuleTemplateRule[]): string | null {
    if (!rules.length) return t('edit.validation.atLeastOneRule');
    for (let ri = 0; ri < rules.length; ri++) {
      const rule = rules[ri];
      if (!rule) continue;
      const validTriggers = rule.triggers.filter(
        tr => tr.dataPoint && tr.connectorType && tr.assetType,
      );
      if (!validTriggers.length)
        return t('edit.validation.triggerRequired', { rule: ri + 1 });
      const validOffsets = rule.offsets.filter(o => o.offsetType && o.metrics);
      if (!validOffsets.length)
        return t('edit.validation.offsetRequired', { rule: ri + 1 });
      for (const o of validOffsets) {
        if ((o.riskFactors ?? []).length === 0)
          return t('edit.validation.offsetImpactRequired', { rule: ri + 1 });
        if (o.trueValue < -5 || o.trueValue > 5)
          return t('edit.validation.trueValueRange', { rule: ri + 1 });
        if (o.falseValue < -5 || o.falseValue > 5)
          return t('edit.validation.falseValueRange', { rule: ri + 1 });
      }
    }
    return null;
  };
}

/** Convert normalized string values to the format the API expects */
const serializeRules = (
  rs: RuleTemplateRule[],
  riskLabels: Record<string, string> = {},
) => {
  const result = rs
    .map((rule, ruleIdx) => {
      const triggers = rule.triggers
        .filter(t => t.dataPoint && t.connectorType && t.assetType)
        .map((t, tIdx) => {
          const isMulti = ['oneOf', 'noneOf', 'allOf'].includes(
            t.connectorType,
          );
          const assetTypeStr =
            typeof t.assetType === 'string' ? t.assetType : '';
          const trigger: Record<string, unknown> = {
            idx: tIdx,
            dataPoint: { key: t.dataPoint, text: t.dataPoint },
            connectorType: { key: t.connectorType, text: t.connectorType },
            assetType: isMulti
              ? [{ key: assetTypeStr, text: assetTypeStr }]
              : { key: assetTypeStr, text: assetTypeStr },
            operator: {
              key: t.operator ?? 'and',
              text: t.operator === 'or' ? 'Or' : 'And',
            },
          };
          if (t.asset) {
            const assetType =
              t.dataPoint === 'AUDIT_FINDINGS'
                ? 'AUDITS'
                : t.dataPoint === 'COMPANY_PROFILES'
                  ? 'COMPANY_PROFILES'
                  : t.dataPoint;
            trigger.asset = { key: t.asset, text: t.asset, type: assetType };
          }
          const tId = (t as { id?: string }).id;
          if (tId) trigger.id = tId;
          return trigger;
        });

      const offsets = rule.offsets
        .filter(
          o => o.offsetType && o.metrics && (o.riskFactors ?? []).length > 0,
        )
        .map((o, oIdx) => {
          const offset: Record<string, unknown> = {
            idx: oIdx,
            offsetType: { key: o.offsetType, text: o.offsetType },
            metrics: { key: o.metrics, text: o.metrics },
            riskFactors: (o.riskFactors ?? []).map(rf => ({
              key: rf,
              text: riskLabels[rf] ?? rf,
            })),
            trueValue: o.trueValue,
            falseValue: o.falseValue,
          };
          const oId = (o as { id?: string }).id;
          if (oId) offset.id = oId;
          return offset;
        });

      const ruleId = (rule as { id?: string }).id;
      return {
        idx: ruleIdx,
        triggers,
        offsets,
        ...(ruleId ? { id: ruleId } : {}),
      };
    })
    .filter(r => r.triggers.length > 0);

  return result.length > 0 ? result : null;
};

// ─── Page ─────────────────────────────────────────────────────────────────────

function RuleEditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { template, metrics } = useRuleTemplate(id);
  const updateMutation = useUpdateRuleTemplate(id);
  const { getTranslation: t } = useTranslator({ page: 'riskRules' });
  const myCompanyId = useSessionStore(s => s.user?.companyId ?? '');

  const validateRules = useMemo(() => makeValidateRules(t), [t]);
  const formSchema = useMemo(
    () =>
      makeFormSchema(
        t('edit.validation.titleRequired'),
        t('edit.validation.formulaRequired'),
      ),
    [t],
  );

  const { data: risksData } = useQuery({
    queryKey: ['risk', 'risks-list'],
    queryFn: fetchAllRisks,
    staleTime: 5 * 60_000,
  });
  const riskLabels = useMemo(
    () => Object.fromEntries((risksData?.data ?? []).map(r => [r.id, r.title])),
    [risksData],
  );

  const [rules, setRules] = useState<RuleTemplateRule[]>([BLANK_RULE()]);
  const [rulesDirty, setRulesDirty] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const hasLoadedRef = useRef(false);

  const {
    watch,
    setValue,
    formState: { errors, isDirty, isSubmitting, isSubmitted, submitCount },
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      tags: [],
      defaultSeverity: 3,
      scoreFormula: '',
      isActive: false,
      visibility: 'PRIVATE' as const,
    },
  });

  // Prefill form on initial load only. Subsequent refetches (after save) must NOT
  // overwrite local state — the API may return stale/empty riskFactors.
  useEffect(() => {
    if (!template.data || hasLoadedRef.current) return;
    const d = template.data;
    // Not editable: not owned, or already activated (activated = locked).
    if (d.createdByCompanyId !== myCompanyId || d.isActive) {
      void navigate({ to: '/risk/rules/$id', params: { id } });
      return;
    }
    hasLoadedRef.current = true;
    reset({
      title: d.title ?? '',
      description: d.description ?? '',
      tags: d.tags ?? [],
      defaultSeverity: d.defaultSeverity ?? 3,
      scoreFormula: d.scoreFormula ?? '',
      isActive: Boolean(d.isActive),
      visibility: (d.visibility as 'PRIVATE' | 'PUBLIC') ?? 'PRIVATE',
    });
    setRules(d.rules?.length ? d.rules : [BLANK_RULE()]);
  }, [template.data, reset, id, navigate, myCompanyId]);

  const values = watch();

  const onSubmit = async (data: FormValues) => {
    const rulesError = validateRules(rules);
    if (rulesError) {
      toast.error(rulesError);
      return;
    }
    try {
      const serializedRules = serializeRules(rules, riskLabels) as unknown as
        | RuleTemplateRule[]
        | null;
      await updateMutation.mutateAsync({
        title: data.title,
        ...(data.description !== undefined
          ? { description: data.description }
          : {}),
        ...(data.tags !== undefined ? { tags: data.tags } : {}),
        defaultSeverity: data.defaultSeverity,
        scoreFormula: serializeFormula(data.scoreFormula ?? ''),
        isActive: data.isActive ? 1 : 0,
        visibility: data.visibility,
        ...(serializedRules ? { rules: serializedRules } : {}),
      });
      setRulesDirty(false);
    } catch {
      /* handled in hook */
    }
  };

  const handleClone = async () => {
    try {
      await bulkCloneRiskRules([id]);
      await queryClient.invalidateQueries({ queryKey: ['risk', 'rules'] });
      toast.success(t('detail.cloneSuccess'));
      void navigate({ to: '/risk/rules' });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const activateRules = serializeRules(rules, riskLabels) as unknown as
    | RuleTemplateRule[]
    | null;
  const activateMutation = useToggleActiveRuleTemplate(
    id,
    {
      title: values.title,
      ...(values.description !== undefined
        ? { description: values.description }
        : {}),
      ...(values.tags !== undefined ? { tags: values.tags } : {}),
      defaultSeverity: values.defaultSeverity,
      scoreFormula: serializeFormula(values.scoreFormula ?? ''),
      visibility: values.visibility,
      ...(activateRules ? { rules: activateRules } : {}),
    },
    t('detail.activateSuccess'),
  );

  const addRule = () => {
    setRules(r => [...r, BLANK_RULE()]);
    setRulesDirty(true);
  };
  const updateRule = (i: number, rule: RuleTemplateRule) => {
    setRules(r => r.map((x, idx) => (idx === i ? rule : x)));
    setRulesDirty(true);
  };
  const deleteRule = (i: number) => {
    setRules(r => {
      const n = r.filter((_, idx) => idx !== i);
      return n.length ? n : [BLANK_RULE()];
    });
    setRulesDirty(true);
  };
  const duplicateRule = (i: number) => {
    setRules(r => {
      const n = [...r];
      n.splice(i + 1, 0, JSON.parse(JSON.stringify(r[i])));
      return n;
    });
    setRulesDirty(true);
  };

  if (template.isLoading) {
    return (
      <PageContainer className='gap-4'>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className='h-32 w-full' />
        ))}
      </PageContainer>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='flex flex-col h-full overflow-hidden'
    >
      {/* Title bar */}
      <div className='px-6 h-[60px] border-b bg-background flex items-center justify-between gap-4 shrink-0'>
        <div className='min-w-0'>
          <h1 className='text-base font-semibold tracking-tight leading-none truncate'>
            {t('edit.title')}
          </h1>
          <p className='text-xs text-muted-foreground leading-none mt-1'>
            {t('edit.subtitle')}
          </p>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <Button
            type='button'
            variant='ghost'
            onClick={handleClone}
            className='gap-1.5'
          >
            <Copy className='size-3.5' /> {t('list.header.clone')}
          </Button>
          <Button
            type='button'
            variant='ghost'
            className='size-9 p-0'
            onClick={() => setHelpOpen(true)}
          >
            <i className='fa-regular fa-circle-question text-sm' />
          </Button>

          {/* Activate button — only when not yet active */}
          {!template.data?.isActive && (
            <AlertDialog>
              <AlertDialogTrigger
                render={
                  <Button
                    type='button'
                    variant='outline'
                    className='gap-1.5'
                    disabled={activateMutation.isPending}
                  />
                }
              >
                <Zap className='size-3.5' />
                {t('detail.activate')}
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('detail.activateDialog.title')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('detail.activateDialog.description')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className='flex justify-end gap-2 pt-2'>
                  <AlertDialogCancel>
                    {t('detail.activateDialog.cancel')}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    variant='default'
                    onClick={() => activateMutation.mutate(1)}
                  >
                    {t('detail.activateDialog.confirm')}
                  </AlertDialogAction>
                </div>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Save button */}
          <Button
            type='submit'
            disabled={isSubmitting || (!isDirty && !rulesDirty)}
            className='gap-1.5'
          >
            <Save className='size-3.5' />
            {isSubmitting ? t('edit.saving') : t('edit.saveChanges')}
          </Button>
        </div>
      </div>

      {/* Errors banner */}
      {Object.keys(errors).length > 0 && (
        <div className='px-6 py-2 bg-destructive/10 border-b border-destructive/20 shrink-0'>
          <p className='text-xs text-destructive font-medium'>
            {errors.title?.message ??
              errors.scoreFormula?.message ??
              errors.defaultSeverity?.message ??
              t('edit.validationError')}
          </p>
        </div>
      )}

      {/* Canvas */}
      <PageContainer className='overflow-y-auto'>
        <div className='max-w-[1100px] mx-auto w-full flex flex-col gap-6'>
          <ValidationSummary
            errors={errors}
            isSubmitted={isSubmitted}
            submitCount={submitCount}
          />
          <TemplateHeaderCard
            key={template.data?.id ?? 'loading'}
            title={values.title}
            description={values.description ?? ''}
            tags={values.tags ?? []}
            defaultSeverity={values.defaultSeverity}
            scoreFormula={values.scoreFormula ?? ''}
            status={template.data?.status ?? null}
            visibility={values.visibility}
            metrics={metrics}
            onTitleChange={v =>
              setValue('title', v, { shouldDirty: true, shouldValidate: true })
            }
            onDescriptionChange={v =>
              setValue('description', v, { shouldDirty: true })
            }
            onTagsChange={v => setValue('tags', v, { shouldDirty: true })}
            onSeverityChange={v =>
              setValue('defaultSeverity', v, { shouldDirty: true })
            }
            onFormulaChange={v =>
              setValue('scoreFormula', v, { shouldDirty: true })
            }
            onVisibilityChange={v =>
              setValue('visibility', v, { shouldDirty: true })
            }
          />

          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2.5'>
              <h2 className='text-sm font-semibold tracking-tight'>
                {t('detail.rules')}
              </h2>
              <span className='inline-flex items-center h-5 px-2 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tabular-nums'>
                {rules.length}
              </span>
            </div>
            <Button
              type='button'
              variant='outline'
              onClick={addRule}
              className='gap-1.5'
            >
              <Plus className='size-3.5' /> {t('edit.addRule')}
            </Button>
          </div>

          <div className='flex flex-col gap-5'>
            {rules.map((rule, i) => (
              <RuleCard
                key={i}
                rule={rule}
                index={i}
                risks={(risksData?.data ?? []).map(r => ({
                  key: r.id,
                  label: r.title,
                }))}
                onChange={r => updateRule(i, r)}
                onDelete={() => deleteRule(i)}
                onDuplicate={() => duplicateRule(i)}
              />
            ))}
          </div>

          <button
            type='button'
            onClick={addRule}
            className='w-full h-[52px] rounded-xl border border-dashed text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50 hover:border-foreground/30 transition-colors flex items-center justify-center gap-2'
          >
            <Plus className='size-4' /> {t('edit.addAnotherRule')}
          </button>
        </div>
      </PageContainer>

      <HelpSheet
        articleId='8960666'
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </form>
  );
}
